<script lang="ts">
  import { cn } from "@/lib/utils";

  export let numberActivities: number;
  export let numberActiveDays: number;
  export let indoorRides: number;
  export let outdoorRides: number;
  export let xertWorkouts: number;
  export let revealed: boolean = false;
  export let color: string = '#5EE6E8';

  const tidy = (n: number) => new Intl.NumberFormat().format(Math.round(n ?? 0));

  // Indoor vs Outdoor split for the activity wheel
  const totalRides = (indoorRides ?? 0) + (outdoorRides ?? 0);
  const indoorPct = totalRides > 0 ? (indoorRides ?? 0) / totalRides : 0;
  const outdoorPct = totalRides > 0 ? (outdoorRides ?? 0) / totalRides : 0;

  // SVG arc math
  const R = 48; // radius in px
  const C = 2 * Math.PI * R; // circumference
  const indoorLen = C * indoorPct;
  const outdoorLen = C * outdoorPct;
</script>

<div class='w-full rounded-2xl p-[1px] activity-card-shell'
  style='box-shadow: 0 10px 28px rgba(0,0,0,0.35), inset 0 0 8px rgba(255,255,255,0.05);'>
  <div class='rounded-2xl activity-card-inner text-off-white'>
    <div class='grid grid-cols-12 gap-4 p-5 overflow-hidden'>
      <!-- Hero wheel: total activities and indoor/outdoor split -->
      <div class='col-span-12 flex flex-col items-center justify-center gap-3'>
        <div class={cn('activity-wheel flex items-center justify-center', revealed ? 'wheel-pop' : '')}>
          <svg width='160' height='160' viewBox='0 0 120 120' class='arc-svg'>
            <!-- base circle -->
            <circle cx='60' cy='60' r={R} class='arc-base'/>
            <!-- indoor arc -->
            <circle cx='60' cy='60' r={R} class='arc-indoor'
              style={`stroke-dasharray:${indoorLen} ${C - indoorLen}; stroke-dashoffset:${revealed ? 0 : C};`}
            />
            <!-- outdoor arc, rotated to start after indoor -->
            <circle cx='60' cy='60' r={R} class='arc-outdoor'
              style={`stroke-dasharray:${outdoorLen} ${C - outdoorLen}; stroke-dashoffset:${revealed ? 0 : C}; transform: rotate(${(indoorPct*360)}deg); transform-origin: 60px 60px;`}
            />
          </svg>
          <div class='wheel-center'>
            <i class='fa-light fa-bicycle text-2xl' style={`color:${color}; text-shadow:${revealed ? `0 0 16px ${color}` : 'none'};`}></i>
            <div class='count'>{tidy(numberActivities)}</div>
            <div class='label'>Activities</div>
          </div>
        </div>
        <div class={cn('micro-legend', revealed ? 'legend-in' : '')}>
          <div class='legend-item'>
            <span class='dot indoor'></span>
            <span>Indoor</span>
          </div>
          <div class='legend-item'>
            <span class='dot outdoor'></span>
            <span>Outdoor</span>
          </div>
        </div>
      </div>

      <!-- Fun stat chips -->
      <div class='col-span-12 flex flex-col gap-3'>
        <div class={cn('stat-chip', revealed ? 'chip-in-1' : '')}>
          <i class='fa-light fa-calendar-star'></i>
          <div class='text'>
            <span class='value'>{tidy(numberActiveDays)}</span>
            <span class='label'>Active Days</span>
          </div>
        </div>
        <div class='grid grid-cols-2 gap-3'>
          <div class={cn('mini text-[#ff4dcd]', revealed ? 'chip-in-2' : '')}>
            <i class='fa-light fa-house text-[#ff4dcd]'></i>
            <div class='metric'>
              <div class='value'>{tidy(indoorRides)}</div>
              <div class='label'>Indoor</div>
            </div>
          </div>
          <div class={cn('mini text-[#7cff4d]', revealed ? 'chip-in-3' : '')}>
            <i class='fa-light fa-sun text-[#7cff4d]'></i>
            <div class='metric'>
              <div class='value'>{tidy(outdoorRides)}</div>
              <div class='label'>Outdoor</div>
            </div>
          </div>
        </div>

        <div class={cn('cta', revealed ? 'cta-bounce' : '')}>
          <div class='left'>
            <i class='fa-light fa-dumbbell' style={`color:${color};`}></i>
            <span>Xert Workouts</span>
          </div>
          <div class='right' style={`color:${color}; text-shadow:${revealed ? `0 0 12px ${color}` : 'none'};`}>{tidy(xertWorkouts)}</div>
        </div>
      </div>
    </div>
  </div>
</div>

<style>
  /* Unique card chrome */
  .activity-card-shell {
    background: linear-gradient(5deg, rgba(18,20,22,1) 60%, rgba(40,44,48,0.18) 85%, rgba(94,230,232,0.10) 100%);
  }
  .activity-card-inner {
    background:
      radial-gradient(circle at 20% 15%, rgba(255,255,255,0.06), transparent 40%),
      radial-gradient(circle at 80% 90%, rgba(255,255,255,0.04), transparent 40%),
      linear-gradient(180deg, rgba(18,20,22,0.95), rgba(8,9,10,0.98));
    border-radius: 1rem;
  }

  /* Hero wheel */
  .activity-wheel { position: relative; width: 160px; height: 160px; opacity: 0; }
  .wheel-pop { animation: wheel-pop 520ms ease-out both; }
  .arc-svg { position: absolute; inset: 0; transform: rotate(-90deg); }
  .arc-base { fill: none; stroke: rgba(255,255,255,0.08); stroke-width: 12; }
  .arc-indoor { fill: none; stroke: #ff4dcd; stroke-width: 12; transition: stroke-dashoffset 600ms ease-out; }
  .arc-outdoor { fill: none; stroke: #7cff4d; stroke-width: 12; transition: stroke-dashoffset 600ms ease-out; }
  .wheel-center { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; }
  .wheel-center .count { font-size: 1.8rem; font-weight: 800; }
  .wheel-center .label { font-size: 0.8rem; opacity: 0.8; }

  .micro-legend { display: flex; gap: 12px; align-items: center; opacity: 0; transform: translateY(8px); }
  .legend-in { animation: legend-in 420ms 360ms ease-out forwards; }
  .legend-item { display: flex; align-items: center; gap: 6px; font-size: 0.8rem; }
  .dot { width: 10px; height: 10px; border-radius: 999px; display: inline-block; box-shadow: 0 0 8px rgba(255,255,255,0.12); }
  .dot.indoor { background: #ff4dcd; }
  .dot.outdoor { background: #7cff4d; }

  /* Chips and metrics */
  .stat-chip {
    border-radius: 12px; border: 1px solid rgba(255,255,255,0.10); background: rgba(255,255,255,0.05);
    padding: 10px 12px; display: flex; align-items: center; gap: 10px; opacity: 0; transform: translateX(-12px);
  }
  .stat-chip i { color: #cfd8dc; text-shadow: 0 0 8px rgba(255,255,255,0.08); }
  .stat-chip .text { display: flex; align-items: baseline; gap: 8px; }
  .stat-chip .value { font-size: 1.4rem; font-weight: 800; }
  .stat-chip .label { font-size: 0.9rem; opacity: 0.85; }

  .mini {
    border-radius: 12px; border: 1px solid rgba(255,255,255,0.10); background: rgba(255,255,255,0.05);
    padding: 10px 12px; display: flex; align-items: center; gap: 10px; opacity: 0; transform: translateY(12px);
  }
  .mini i { color: #cfd8dc; text-shadow: 0 0 8px rgba(255,255,255,0.08); }
  .mini .metric .value { font-size: 1.25rem; font-weight: 700; }
  .mini .metric .label { font-size: 0.8rem; opacity: 0.8; }

  .cta {
    width: 100%; border-radius: 12px; border: 1px solid rgba(255,255,255,0.10); background: rgba(255,255,255,0.05);
    padding: 12px; display: flex; align-items: center; justify-content: space-between; opacity: 0; transform: translateY(12px);
  }
  .cta .left { display: flex; align-items: center; gap: 8px; }
  .cta .right { font-size: 1.6rem; font-weight: 800; }

  /* Animations */
  .chip-in-1 { animation: chip-left 420ms 120ms ease-out forwards; }
  .chip-in-2 { animation: chip-up 420ms 200ms ease-out forwards; }
  .chip-in-3 { animation: chip-up 420ms 280ms ease-out forwards; }
  .cta-bounce { animation: cta-rise 480ms 360ms ease-out forwards; }

  @keyframes wheel-pop {
    0% { opacity: 0; transform: scale(0.92) rotate(-6deg); }
    70%{ opacity: 1; transform: scale(1.02) rotate(0deg); }
    100%{ opacity: 1; transform: scale(1) rotate(0deg); }
  }
  @keyframes legend-in {
    0% { opacity: 0; transform: translateY(8px); }
    100%{ opacity: 1; transform: translateY(0); }
  }
  @keyframes chip-left {
    0% { opacity: 0; transform: translateX(-12px); }
    100%{ opacity: 1; transform: translateX(0); }
  }
  @keyframes chip-up {
    0% { opacity: 0; transform: translateY(12px); }
    100%{ opacity: 1; transform: translateY(0); }
  }
  @keyframes cta-rise {
    0% { opacity: 0; transform: translateY(12px) scale(0.98); }
    60%{ opacity: 1; transform: translateY(-2px) scale(1.01); }
    100%{ opacity: 1; transform: translateY(0) scale(1); }
  }
</style>
